<script setup>
import {defineProps, defineEmits, defineModel, ref, computed} from 'vue'
import {getValidationError} from '@shared/utils/helpers'
import {notify} from '@kyvg/vue3-notification'
import {useDefaultStore} from '@/stores'

const modelValue = defineModel({required: true})

const {uploadFile} = useDefaultStore()

// Props & emits
const emit = defineEmits(['file-uploaded'])
const props = defineProps({
  usageType: {
    type: String,
    default: 'file',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    default: 'media-upload-license',
  },
  required: {
    type: Boolean,
    default: false,
  },
  hideTools: {
    type: Boolean,
    default: false,
  },
  isObjectAssignFile: {
    type: Boolean,
    default: true,
  },
})

// Refs
const fileLoading = ref(false)
const baseAPI = process.env.VUE_APP_API_URL

// Computed
const isValid = computed(() => !props.required || !!modelValue.value)

// Methods
const handleUploadFile = async payload => {
  fileLoading.value = true

  try {
    return await uploadFile(payload)
  } catch (error) {
    notify({type: 'error', text: getValidationError(error?.data?.message)})
  }
}

const onFileUploaded = async event => {
  let response

  if (!event.target.files[0]) {
    return
  }

  const requestFormData = new FormData()

  requestFormData.set('usage_type', props.usageType)
  requestFormData.set('file', event.target.files[0], event.target.files[0].name)

  response = await handleUploadFile(requestFormData)

  if (!response) {
    return (fileLoading.value = false)
  }

  if (modelValue.value && props.isObjectAssignFile) {
    Object.assign(modelValue.value, response, {hash: modelValue.value?.hash || response.hash})
  } else {
    modelValue.value = cloneSafe(response)
  }

  emit('file-uploaded', response, requestFormData)
  fileLoading.value = false
}
</script>

<template>
  <div v-if="modelValue?.file_url || canEdit" class="text-center position-relative">
    <div v-if="fileLoading" class="full-width btn btn-secondary d-flex align-center justify-content-center">
      <div class="file-preloader"></div>
    </div>

    <label
      v-else-if="!modelValue?.hash || (modelValue?.hash && !modelValue.file_url)"
      :for="id"
      class="full-width btn btn-secondary row-height"
      :class="isValid ? 'valid' : 'invalid'"
    >
      <span class="text-sm">Upload</span> <i class="fa-light fa-cloud-arrow-up"></i>
    </label>

    <div v-else-if="modelValue?.hash && modelValue?.file_url" class="d-flex custom gap-3 pointer">
      <label
        :for="id"
        class="full-width btn btn-secondary row-height d-flex justify-content-center"
        :class="{'is-disabled': disabled}"
      >
        <span class="max-w-290px text-ellipsis">{{ modelValue?.name }}</span>
      </label>

      <div v-if="!hideTools" class="input-height">
        <a :href="modelValue?.file_url" class="form-icon-btn h-100" target="_blank" rel="nofollow noopener noreferrer">
          <i class="fa-light fa-eye"></i>
        </a>
      </div>
      <div v-if="!hideTools" class="input-height">
        <a
          :href="`${baseAPI}/file/${modelValue?.hash}/download`"
          class="form-icon-btn h-100"
          target="_blank"
          rel="nofollow noopener noreferrer"
        >
          <i class="fa-light fa-download"></i>
        </a>
      </div>
    </div>

    <input
      class="d-none"
      @change="onFileUploaded"
      :id="id"
      type="file"
      :disabled="disabled || fileLoading"
      :required="required"
    />
  </div>
</template>

<style lang="scss">
label.btn {
  margin-bottom: 0 !important;
}
</style>
